import React from 'react';

import ExpandButton from '@/components/ExpandButton';

import useDescriptionExpandable from './useDescriptionExpandable';
import {
  Container,
  DefaultLayout,
  DefaultContent,
  CardControl,
  ItemHeading,
  ItemTitle,
  ItemContent,
  ActionArea,
  DetailsContainer,
  DetailsHeading,
  DefaultSection,
  BlockOverlay,
} from './styles';
import {
  CardItemDisabledMode,
  CardItemRender,
  CardItemRenderProps,
} from './types';

export interface CardItemProps extends React.PropsWithChildren {
  // Main content
  title: string | CardItemRender;
  description?: string | CardItemRender;
  // State and mode
  state?: 'marked' | 'unmarked';
  mode?: 'default' | 'details';
  variant?: 'default' | 'added';
  // Attached components
  renderActions?: CardItemRender;
  renderDetails?: CardItemRender;
  // Status and permissions
  loading?: boolean;
  disabledMode?: CardItemDisabledMode;
  canExpand?: boolean;
  canSelect?: boolean;
  // Events
  onClick?: React.MouseEventHandler<HTMLElement>;
}

interface DetailsProps extends React.PropsWithChildren {
  title?: React.ReactNode;
}

const ItemDetails: React.FC<DetailsProps> = ({ title, children }) => {
  return (
    <DetailsContainer>
      <DetailsHeading>{title}</DetailsHeading>
      {children}
    </DetailsContainer>
  );
};

const CardItem: React.FC<CardItemProps> = ({
  title,
  description,
  variant = 'default',
  mode = 'default',
  state = 'unmarked',
  renderActions,
  renderDetails,
  children,
  loading = false,
  disabledMode = 'none',
  canExpand = true,
  canSelect = true,
  onClick,
}) => {
  const {
    ref: descriptionRef,
    descriptionState,
    toggle,
  } = useDescriptionExpandable({ description });

  const selectionDisabled = React.useMemo(() => {
    return disabledMode === 'selection' || disabledMode === 'all';
  }, [disabledMode]);

  const cardDisabled = React.useMemo(() => {
    return disabledMode === 'all';
  }, [disabledMode]);

  const renderProps = React.useMemo<CardItemRenderProps>(
    () => ({
      state,
      mode,
      variant,
      loading,
      disabledMode,
      selectionDisabled,
      cardDisabled,
      canExpand,
      canSelect,
      descriptionState,
    }),
    [
      state,
      mode,
      variant,
      loading,
      disabledMode,
      selectionDisabled,
      cardDisabled,
      canExpand,
      canSelect,
      descriptionState,
    ],
  );

  const renderedDescription = React.useMemo(() => {
    return typeof description === 'function'
      ? description(renderProps)
      : description;
  }, [description, renderProps]);

  const renderedTitle = React.useMemo(() => {
    return typeof title === 'function' ? title(renderProps) : title;
  }, [title, renderProps]);

  const renderedActions = React.useMemo(() => {
    return renderActions?.(renderProps);
  }, [renderActions, renderProps]);

  const renderedDetails = React.useMemo(() => {
    return renderDetails?.(renderProps);
  }, [renderDetails, renderProps]);

  const hasExpandPermission = React.useMemo(() => {
    return !!renderedDescription && canExpand;
  }, [renderedDescription, canExpand]);

  return (
    <Container itemState={state} variant={variant} blocked={cardDisabled}>
      {/* Default mode */}
      {mode === 'default' && (
        <DefaultSection>
          <DefaultLayout>
            <DefaultContent>
              <CardControl
                disabled={selectionDisabled}
                state={state}
                readOnly={!canSelect}
                onClick={onClick}
                loading={loading}
              >
                {({ focusState }) => (
                  <ItemHeading elemState={focusState} itemState={state}>
                    <ItemTitle>{renderedTitle}</ItemTitle>
                  </ItemHeading>
                )}
              </CardControl>

              {renderedDescription && (
                <ItemContent
                  ref={descriptionRef}
                  expanded={descriptionState.expanded}
                >
                  {typeof renderedDescription === 'string' ? (
                    <p>{renderedDescription}</p>
                  ) : (
                    renderedDescription
                  )}
                </ItemContent>
              )}
            </DefaultContent>

            <ActionArea>
              {renderedActions}

              {/* Default actions */}
              {hasExpandPermission && (
                <ExpandButton
                  expanded={descriptionState.expanded}
                  disabled={
                    !renderedDescription || !descriptionState.expandable
                  }
                  onClick={() => toggle()}
                />
              )}
            </ActionArea>
          </DefaultLayout>

          {children}
        </DefaultSection>
      )}

      {/* Details mode */}
      {mode === 'details' && (
        <ItemDetails title={renderedTitle}>{renderedDetails}</ItemDetails>
      )}

      {/* Block overlay */}
      {cardDisabled && <BlockOverlay />}
    </Container>
  );
};

export default CardItem;
